
import React, { useState, useRef, FC, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Divider,
  Tooltip,
  useMediaQuery,
  ListItemButton,
} from "@mui/material";
import TermoCard from "./TermoCard";
import { LocalStorage } from "src/framework/commons/LocalStorage";
import jwtDecode from "jwt-decode";
import { ContaUsuario } from "src/store/global.store.types";
import api, { IDataRequest } from "src/framework/providers/api";
import moment from "moment";
import { useObservable } from "src/framework/store/storeUtil";
import { CacheType, contaUsuario$ } from "src/store/global.store";
import { TermoPendente } from "src/store/termos/types";
import theme from "src/theme/light";
import "./styles.css";
import { LoginStore } from "src/framework/store/LoginStore";

interface TermosAtivosProps {
  termos: TermoPendente[];
  open: boolean;
  onConfirm: (termos: number[]) => void;
}

const TermosAtivos: FC<TermosAtivosProps> = (props) => {
  const [termoSelecionado, setTermoSelecionado] = useState(props.termos[0]);
  const [aceitos, setAceitos] = useState<number[]>([]);
  const userInfo = LocalStorage.get("solar.userExtraInfo", true);
  const contaUsuario = useObservable<CacheType<ContaUsuario | null>>(contaUsuario$);

  const scrollBoxRef = useRef<HTMLDivElement>(null);

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleAccept = (id: number) => {
    setAceitos((prev) => {
        const novosAceitos = prev.includes(id) ? prev : [...prev, id];
        const proximoTermoNaoAceito = props.termos.find(t => !novosAceitos.includes(t?.id as number));

        if (proximoTermoNaoAceito) {
            setTermoSelecionado(proximoTermoNaoAceito);
        }

        return novosAceitos;
    });
  };

  const handleLogout = async () => {
    const token = LocalStorage.get("solar.token")
    const decoded: any = jwtDecode(token)

    if (decoded?.type !== 'conta') {
      window.location.href = `${window.location.origin}/signout`
      return
    }

    const redirectURL = await LoginStore.doLogOutConta()
    if (redirectURL) window.location.href = redirectURL
  }
  const handleContinuar = async () => {
    const accountUser = contaUsuario?.data?.conta;
    const tipoContaId = accountUser?.tipo_conta_id;
    //confirmacao de termos
    const request: IDataRequest = {
      url: "/termos/usuario/confirmar",
      data: {
        termos: aceitos,
        confirmado_termo_por: userInfo?.userId,
        data_confirmacao_termo: moment().format("YYYY-MM-DD HH:mm:ss"), 
        tipo_conta_id: userInfo?.tipoContaId || tipoContaId,
      }
    }
    await api.post(request);
    props.onConfirm(aceitos)
  }

  const todosAceitos = useMemo(()=>{
    return props?.termos?.length === aceitos?.length;
  },[aceitos])

  return (
    <Dialog open={props.open} fullWidth maxWidth="lg" className="modal-termos">
      <Box 
        display="flex" 
        height={isMobile ? "100%" : "75vh"} 
        flexDirection={isMobile ? "column" : "row"}
      >
        <Box
          width={isMobile ? "100%" : "280px"}
          bgcolor="#e5e5e5"
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          height={isMobile ? "32vh" : "100%"}
          paddingTop={"26px"}
        >
          <List disablePadding className="lista-termos">
            {props?.termos.map((termo, index) => (
              <ListItem disablePadding key={termo?.id}>
                <ListItemButton
                  selected={termoSelecionado?.id === termo?.id}
                  onClick={() => {
                    setTermoSelecionado(termo);
                    if (scrollBoxRef.current) scrollBoxRef.current.scrollTop = 0;
                  }}
                  sx={{
                    backgroundColor:
                      termoSelecionado?.id === termo?.id ? "#cfcfcf" : "#e5e5e5",
                    "&:hover": {
                      backgroundColor:
                        termoSelecionado?.id === termo?.id ? "#cfcfcf" : "#dcdcdc",
                    },
                  }}
                >
                  <ListItemIcon>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      width={24}
                      height={24}
                      borderRadius="50%"
                      fontSize={13}
                      fontWeight="bold"
                      color="#fff"
                      sx={{
                        backgroundColor: aceitos?.includes(termo?.id as number)
                          ? "green"
                          : "#bfbfbf",
                      }}
                    >
                      {index + 1}
                    </Box>
                  </ListItemIcon>

                  <ListItemText
                    primary={termo.versao}
                    primaryTypographyProps={{ fontSize: 14 }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>

        <Box flex={1} display="flex" flexDirection="column" height="100%">
          <DialogContent
            style={{
              flex: 1,
              overflowY: "auto",
              padding: isMobile ? "15px" : "24px",
            }}
            dividers
            ref={scrollBoxRef}
          >
            <TermoCard 
              id={termoSelecionado?.id as number}
              texto={termoSelecionado?.descricao}
              onAccept={(id) => {
                handleAccept(id)
              }}
              aceitos={aceitos}
            />
          </DialogContent>
          <Divider style={{boxShadow: '0 -2px 5px rgba(0, 0, 0, 0.15)', border: "none"}}/>
          <DialogActions style={{ padding: "16px 24px" }}>
            <Button variant="contained" color="secondary" onClick={handleLogout}>
              Cancelar
            </Button>
            {todosAceitos ? (<>
              <Button
                variant="contained"
                color="primary"
                disabled={false}
                onClick={handleContinuar}
              >
                Continuar
              </Button>

            </>):(<>
              <Tooltip title="Aceite todos os termos para prosseguir" placement="top" arrow>
                <span>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={true}
                    onClick={handleContinuar}
                  >
                    Continuar
                  </Button>
                </span>
              </Tooltip>
            </>)}
          </DialogActions>
        </Box>
      </Box>
    </Dialog>
  );
}
export {TermosAtivos}
